/**
 * Interface representing a single OHLC (Open, High, Low, Close) data point.
 */
export interface OHLC {
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

/**
 * Feature sets generated from OHLC time series.
 */
export interface Features {
  /** Relative Strength Index values */
  rsi: number[];
  /** Moving Average Convergence Divergence values */
  macd: {
    macd: number[];
    signal: number[];
    hist: number[];
  };
  /** Bollinger Bands values */
  bollinger: {
    lower: number[];
    middle: number[];
    upper: number[];
  };
}

/**
 * Generate technical indicators (RSI, MACD, Bollinger Bands) from OHLC series.
 * @param ohlc Array of OHLC data points.
 * @param rsiPeriod Lookback period for RSI (default 14).
 * @param macdFast Fast EMA period for MACD (default 12).
 * @param macdSlow Slow EMA period for MACD (default 26).
 * @param macdSignal Signal line EMA period for MACD (default 9).
 * @param bbPeriod Lookback period for Bollinger Bands (default 20).
 * @param bbStdDev Standard deviation multiplier for Bollinger Bands (default 2).
 * @returns Promise resolving to calculated features.
 */
// Simple RSI calculation for browser compatibility
function calculateRSI(prices: number[], period = 14): number[] {
  if (prices.length < period + 1) {
    return Array(prices.length).fill(50); // Default RSI of 50
  }

  const rsi: number[] = [];
  let avgGain = 0;
  let avgLoss = 0;

  // Pre-fill neutral values to keep length consistent with other indicators
  for (let i = 0; i < period; i++) {
    rsi.push(50); // Neutral RSI value for initial periods
  }

  // Calculate initial gains and losses
  for (let i = 1; i <= period; i++) {
    const change = prices[i] - prices[i - 1];
    if (change > 0) avgGain += change;
    else avgLoss += Math.abs(change);
  }
  
  avgGain /= period;
  avgLoss /= period;

  // Calculate RSI for the remaining periods
  for (let i = period; i < prices.length; i++) {
    // Handle division by zero properly: if avgLoss = 0, RSI should be 100
    const rs = avgLoss === 0 ? Number.POSITIVE_INFINITY : avgGain / avgLoss;
    const rsiValue = 100 - (100 / (1 + rs));
    rsi.push(rsiValue);

    // Update averages for next iteration
    if (i < prices.length - 1) {
      const change = prices[i + 1] - prices[i];
      avgGain = ((avgGain * (period - 1)) + Math.max(0, change)) / period;
      avgLoss = ((avgLoss * (period - 1)) + Math.max(0, -change)) / period;
    }
  }

  return rsi;
}

// Simple MACD calculation
function calculateMACD(prices: number[], fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) {
  if (prices.length < slowPeriod) {
    return {
      macd: Array(prices.length).fill(0),
      signal: Array(prices.length).fill(0),
      hist: Array(prices.length).fill(0)
    };
  }

  // Calculate EMAs
  const fastEMA = calculateEMA(prices, fastPeriod);
  const slowEMA = calculateEMA(prices, slowPeriod);
  
  const macd = fastEMA.map((fast, i) => fast - slowEMA[i]);
  const signal = calculateEMA(macd, signalPeriod);
  const hist = macd.map((m, i) => m - signal[i]);

  return { macd, signal, hist };
}

// Helper function to calculate EMA
function calculateEMA(prices: number[], period: number): number[] {
  const k = 2 / (period + 1);
  const ema = [prices[0]];
  
  for (let i = 1; i < prices.length; i++) {
    ema.push(prices[i] * k + ema[i - 1] * (1 - k));
  }
  
  return ema;
}

// Simple Bollinger Bands calculation with consistent array lengths
function calculateBollingerBands(prices: number[], period = 20, stdDev = 2) {
  if (prices.length < period) {
    return {
      lower: Array(prices.length).fill(prices[0] * 0.95),
      middle: Array(prices.length).fill(prices[0]),
      upper: Array(prices.length).fill(prices[0] * 1.05)
    };
  }

  const middle: number[] = [];
  const lower: number[] = [];
  const upper: number[] = [];

  // Fill initial values for consistency with other indicators
  for (let i = 0; i < period - 1; i++) {
    const slice = prices.slice(0, i + 1);
    const avg = slice.reduce((sum, p) => sum + p, 0) / (i + 1);
    middle.push(avg);
    lower.push(avg * 0.98); // Conservative bands for initial values
    upper.push(avg * 1.02);
  }

  // Calculate full Bollinger Bands for remaining data
  for (let i = period - 1; i < prices.length; i++) {
    const slice = prices.slice(i - period + 1, i + 1);
    const avg = slice.reduce((sum, price) => sum + price, 0) / period;
    const variance = slice.reduce((sum, price) => sum + Math.pow(price - avg, 2), 0) / period;
    const sd = Math.sqrt(variance);

    middle.push(avg);
    lower.push(avg - (stdDev * sd));
    upper.push(avg + (stdDev * sd));
  }

  return { lower, middle, upper };
}

export async function engineerFeatures(
  ohlc: OHLC[],
  rsiPeriod = 14,
  macdFast = 12,
  macdSlow = 26,
  macdSignal = 9,
  bbPeriod = 20,
  bbStdDev = 2
): Promise<Features> {
  try {
    const closes = ohlc.map(d => d.close);

    // Use browser-compatible calculations
    const rsi = calculateRSI(closes, rsiPeriod);
    const macd = calculateMACD(closes, macdFast, macdSlow, macdSignal);
    const bollinger = calculateBollingerBands(closes, bbPeriod, bbStdDev);

    return {
      rsi,
      macd,
      bollinger,
    };
  } catch (error) {
        console.warn('Error calculating technical indicators, using defaults:', error);

    // Return default values if calculation fails - ensure non-negative length
    const defaultLength = Math.max(0, ohlc.length - Math.max(rsiPeriod, bbPeriod, macdSlow));
    return {
      rsi: Array(defaultLength).fill(50),
      macd: {
        macd: Array(defaultLength).fill(0),
        signal: Array(defaultLength).fill(0),
        hist: Array(defaultLength).fill(0),
      },
      bollinger: {
        lower: Array(defaultLength).fill(ohlc[0]?.close * 0.95 || 100),
        middle: Array(defaultLength).fill(ohlc[0]?.close || 100),
        upper: Array(defaultLength).fill(ohlc[0]?.close * 1.05 || 100),
      },
    };
  }
}